import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Pencil } from "lucide-react";
import { AddTaskDialog } from "@/components/crm/LeadExtras";
import { cached, remember } from "@/lib/cache";
import { fmtRel, dueTone, DUE_TEXT, fmtDue } from "@/lib/fmt";
import { Empty } from "@/components/ui-helpers";
import OutcomeDialog from "@/components/crm/OutcomeDialog";
import { useAuth } from "@/context/AuthContext";

const endOfToday = () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d.toISOString(); };

function group(rows, status) {
  if (status !== "open") return [["Completed", rows]];
  const eod = endOfToday();
  return [
    ["Overdue", rows.filter((t) => dueTone(t.due) === "late")],
    ["Today", rows.filter((t) => dueTone(t.due) === "today")],
    ["Upcoming", rows.filter((t) => t.due && t.due > eod)],
    ["No due date", rows.filter((t) => !t.due)],
  ].filter(([, r]) => r.length);
}

function TaskRow({ t, onOpen, onComplete, onEdit }) {
  const tone = t.status === "open" ? dueTone(t.due) : "";
  const late = tone === "late";
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-4 py-3 hover:bg-[var(--hover)] ${late ? "shadow-[inset_3px_0_0_var(--breach)]" : tone ? "shadow-[inset_3px_0_0_var(--warn)]" : ""}`} data-testid={`task-row-${t._id}`} data-due-tone={tone || undefined}>
      <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onOpen(t.lead_id)} data-testid={`task-open-lead-${t._id}`}>
        <div className="text-sm font-medium text-[var(--text)] break-words">{t.label}</div>
        <div className="text-xs text-[var(--text-dim)] mt-0.5 break-words">{t.lead_name || "—"}{t.lead_stage ? ` · ${t.lead_stage}` : ""} · {t.owner || "unassigned"}</div>
      </button>
      <div className={`text-xs whitespace-nowrap ${DUE_TEXT[tone] || "text-[var(--text-dim)]"}`} title={t.due ? fmtDue(t) : ""}>
        {t.status === "done" ? `done · ${t.outcome || ""}` : t.due ? `${late ? "overdue · " : "due "}${fmtDue(t)}` : "no due date"}
      </div>
      <button className="btn text-xs self-start sm:self-auto" onClick={() => onEdit(t)} data-testid={`task-edit-${t._id}`}><Pencil size={12} />Edit</button>
      {t.status === "open" && <button className="btn text-xs self-start sm:self-auto" onClick={() => onComplete(t)} data-testid={`task-complete-${t._id}`}>Complete</button>}
    </div>
  );
}

export default function TasksView({ cadence, onOpen, refreshKey, onChanged }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [scope, setScope] = useState(localStorage.getItem("tasks_scope") || "all");
  const [status, setStatus] = useState("open");
  const [task, setTask] = useState(null);
  const [edit, setEdit] = useState(null);
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/crm/users").then((r) => setUsers(r.data.rows)); }, []);
  const tkey = `tasks:${scope}:${status}`;
  const load = () => api.get("/crm/tasks", { params: { status, owner: scope === "mine" ? user.email : undefined } }).then((r) => setRows(remember(tkey, r.data.rows)));
  useEffect(() => { const hit = cached(tkey); if (hit) setRows(hit); load(); }, [scope, status, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickScope = (s) => { localStorage.setItem("tasks_scope", s); setScope(s); };

  return (
    <div data-testid="tasks-view">
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
        {["all", "mine"].map((s) => <button key={s} onClick={() => pickScope(s)} className={`btn ${scope === s ? "btn-primary" : ""}`} data-testid={`tasks-scope-${s}`}>{s === "all" ? "Everyone" : "Mine"}</button>)}
        <span className="w-px h-4 bg-[var(--border)] mx-1" />
        {["open", "done"].map((s) => <button key={s} onClick={() => setStatus(s)} className={`btn capitalize ${status === s ? "btn-primary" : ""}`} data-testid={`tasks-status-${s}`}>{s}</button>)}
        {rows && <span className="ml-auto text-sm text-[var(--text-dim)]" data-testid="tasks-count"><b className="text-[var(--text)]">{rows.length}</b> task{rows.length === 1 ? "" : "s"}</span>}
      </div>
      {!rows ? <div className="text-xs text-[var(--text-faint)]">Loading…</div> : !rows.length ? <Empty label="Nothing here" /> : (
        <div className="space-y-5">
          {group(rows, status).map(([g, list]) => (
            <section key={g} data-testid={`tasks-group-${g.toLowerCase().replace(/\s/g, "-")}`}>
              <h3 className={`section-label mb-2 ${g === "Overdue" ? "!text-[var(--breach)]" : ""}`}>{g} · {list.length}</h3>
              <div className="panel rounded-lg divide-y divide-[var(--border)] overflow-hidden">
                {list.map((t) => <TaskRow key={t._id} t={t} onOpen={onOpen} onComplete={setTask} onEdit={setEdit} />)}
              </div>
            </section>
          ))}
        </div>
      )}
      {task && <OutcomeDialog task={task} cadence={cadence} onClose={() => setTask(null)} onDone={() => { setTask(null); load(); onChanged?.(); }} />}
      {edit && <AddTaskDialog task={edit} lead={null} users={users} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); onChanged?.(); }} />}
    </div>
  );
}
